import { SourceDataEventContext, SourceDataFormContext, SourceDataPoint, SourceDataReview, SourceDataVerification, SourceDataNotApplicable, UpdateStatusDataType, UpdateStatusEvent, UpdateStatusEventType, UpdatedCaseReportFormStateEvent, UpdatedSourceDataReviewEvent, UpdatedSourceDataVerificationEvent, UpdatedSourceDataNotApplicableEvent } from '@/api/dtos';
import { AppSettings } from '@/AppSettings';
import CollapsiblePanel from '@/components/layouts/CollapsiblePanel';
import useSignalROnMessage from '@/hooks/api/useSignalROnMessage';
import useCaseReportFormState from '@/hooks/data/useCaseReportFormState';
import { HubConnection } from '@microsoft/signalr/dist/esm';
import { Box } from '@mui/material';
import { Form as CoreForm, Field, FormErrorBoundary, useFormActions } from '@ngt/forms-core';
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import FormDirty from './state/FormDirty';
import FormErrors from './state/FormErrors';
import FormFields from './state/FormFields';
import FormFocused from './state/FormFocused';
import FormInitialValue from './state/FormInitialValue';
import FormState from './state/FormState';
import FormTouched from './state/FormTouched';
import FormValue from './state/FormValue';

export interface IReviewAndVerificationForm {

}

export interface IReviewAndVerificationFormProps<TFormValue extends object> extends PropsWithChildren {
    trialId?: number, 
    masterGroupId?: number,
    coordinatingGroupId?: number,
    institutionId?: number,
    participantId?: number,
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,

    debug?: boolean;
}

export interface IReviewAndVerificationItemData {
    event: SourceDataEventContext,
    form: SourceDataFormContext,
    item: SourceDataPoint,
    review?: SourceDataReview; 
    verification?: SourceDataVerification;
    notApplicable?: SourceDataNotApplicable;
    reviewLoading: boolean;
    verificationLoading: boolean;
    notApplicableLoading: boolean;
}

interface IReviewAndVerificationFormUpdaterProps {
    
    trialId?: number, 
    masterGroupId?: number,
    coordinatingGroupId?: number,
    institutionId?: number,
    participantId?: number,
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
}

const ReviewAndVerificationFormUpdater: FC<IReviewAndVerificationFormUpdaterProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid
}) => {
    const {setFieldValue} = useFormActions();

    const onReviewMessage = useCallback(async (connection: HubConnection, data: UpdatedSourceDataReviewEvent) => {
        if (
            trialId === data.trialId &&
            participantId === data.participantId &&
            (!studyEventOid || studyEventOid === data.studyEventOid) &&
            (!studyEventRepeat || studyEventRepeat === data.studyEventRepeat) &&
            (!caseReportFormOid || caseReportFormOid === data.caseReportFormOid)
        )
        {
            const propertyPath = `data.${data.studyEventOid}.${data.studyEventRepeat}.${data.caseReportFormOid}.${data?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey}.${data.itemGroupRepeat}.${data.itemName}.review`;

            setFieldValue<SourceDataReview>(propertyPath, current => {
                if (!current) {
                    return new SourceDataReview({
                        ...data,
                        id: data.sourceDataReviewId
                    })
                }

                return new SourceDataReview({
                    ...current,
                    ...data
                })
            }, true, true, true, true)
        }

    }, [
        setFieldValue,
        trialId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid
    ]);

    const onVerificationMessage = useCallback(async (connection: HubConnection, data: UpdatedSourceDataVerificationEvent) => {
        if (
            trialId === data.trialId &&
            participantId === data.participantId &&
            (!studyEventOid || studyEventOid === data.studyEventOid) &&
            (!studyEventRepeat || studyEventRepeat === data.studyEventRepeat) &&
            (!caseReportFormOid || caseReportFormOid === data.caseReportFormOid)
        )
        {
            const propertyPath = `data.${data.studyEventOid}.${data.studyEventRepeat}.${data.caseReportFormOid}.${data?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey}.${data.itemGroupRepeat}.${data.itemName}.verification`;

            setFieldValue<SourceDataVerification>(propertyPath, current => {
                if (!current) {
                    return new SourceDataVerification({
                        ...data,
                        id: data.sourceDataVerificationId
                    })
                }

                return new SourceDataVerification({
                    ...current,
                    ...data
                })
            }, true, true, true, true)
        }

    }, [
        setFieldValue,
        trialId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid
    ]);

    const onNotApplicableMessage = useCallback(async (connection: HubConnection, data: UpdatedSourceDataNotApplicableEvent) => {
        if (
            trialId === data.trialId &&
            participantId === data.participantId &&
            (!studyEventOid || studyEventOid === data.studyEventOid) &&
            (!studyEventRepeat || studyEventRepeat === data.studyEventRepeat) &&
            (!caseReportFormOid || caseReportFormOid === data.caseReportFormOid)
        )
        {
            const propertyPath = `data.${data.studyEventOid}.${data.studyEventRepeat}.${data.caseReportFormOid}.${data?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey}.${data.itemGroupRepeat}.${data.itemName}.not-applicable`;

            setFieldValue<SourceDataNotApplicable>(propertyPath, current => {
                if (!current) {
                    return new SourceDataNotApplicable({
                        ...data,
                        id: data.sourceDataNotApplicableId
                    })
                }

                return new SourceDataNotApplicable({
                    ...current,
                    ...data
                })
            }, true, true, true, true)
        }

    }, [
        setFieldValue,
        trialId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid
    ]);

    

    const onCaseReportFormMessage = useCallback(async (connection: HubConnection, data: UpdatedCaseReportFormStateEvent) => {
        if (
            trialId === data.trialId &&
            participantId === data.participantId &&
            (!studyEventOid || studyEventOid === data.studyEventOid) &&
            (!studyEventRepeat || studyEventRepeat === data.studyEventRepeat) &&
            (!caseReportFormOid || caseReportFormOid === data.caseReportFormOid)
        )
        {
            for (let update of data.updates) {
                const propertyPath = `data.${data.studyEventOid}.${data.studyEventRepeat}.${data.caseReportFormOid}.${update?.point?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey}.${update.point?.itemGroupRepeat}.${update.point?.itemName}`;
    
                setFieldValue<IReviewAndVerificationItemData>(propertyPath, current => {
                    if (!current) {
                        return {
                            event: new SourceDataEventContext({
                                id: undefined,
                                trialId: data.trialId,
                                masterGroupId: data.masterGroupId,
                                coordinatingGroupId: data.coordinatingGroupId,
                                institutionId: data.institutionId,
                                participantId: data.participantId,
                                studyEventOid: data.studyEventOid,
                                studyEventRepeat: data.studyEventRepeat
                            }),
                            form: new SourceDataFormContext({
                                id: undefined,
                                sourceDataEventContextId: undefined,
                                caseReportFormOid: data.caseReportFormOid,
                                caseReportFormVersionOid: data.caseReportFormVersionOid
                            }),
                            item: update.point!,
                            reviewLoading: false,
                            verificationLoading: false,
                            notApplicableLoading: false,
                            review: update.review,
                            verification: update.verification,
                            notApplicable: update.notApplicable
                        };
                    }
    
                    return {
                        ...current,
                        item: update.point!,
                        review: update.review,
                        verification: update.verification,
                        notApplicable: update.notApplicable,
                    }
                }, true, true, true, true)
            }
        }

    }, [
        setFieldValue,
        trialId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid
    ]);

    useSignalROnMessage(AppSettings.channels.review, onReviewMessage);
    useSignalROnMessage(AppSettings.channels.verification, onVerificationMessage);
    useSignalROnMessage(AppSettings.channels.notApplicable, onNotApplicableMessage);
    useSignalROnMessage(AppSettings.channels.caseReportFormState, onCaseReportFormMessage);

    return null;
}

const defaultValue = {};

const ReviewAndVerificationForm = <TFormValue extends object>({
    debug,
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    children
}: IReviewAndVerificationFormProps<TFormValue>) => {

    const { 
        data: states,
        loading: statesLoading,
        validating: statesValidating,
        refresh: statesRefresh
    } = useCaseReportFormState(
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid
    );


    const onStatusMessage = useCallback(async (connection: HubConnection, data: UpdateStatusEvent) => {
        if (
            trialId === data.trialId &&
            data.type === UpdateStatusEventType.Complete &&
            data.dataType === UpdateStatusDataType.All
        )
        {
            await statesRefresh();
        }

    }, [
        trialId,
        statesRefresh
    ]);

    useSignalROnMessage(AppSettings.channels.updateStatus, onStatusMessage);

    const data = useMemo(() => {
        const newData: Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, IReviewAndVerificationItemData>>>>>>  = {};

        for (let studyEventOid in states) {
            newData[studyEventOid] = {};

            for (let studyEventRepeat in states[studyEventOid]) {
                newData[studyEventOid][studyEventRepeat] = {};

                for (let caseReportFormOid in states[studyEventOid][studyEventRepeat].forms) {
                    newData[studyEventOid][studyEventRepeat][caseReportFormOid] = {};

                    for (let itemGroupName in states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].items) {
                        newData[studyEventOid][studyEventRepeat][caseReportFormOid][itemGroupName] = {};

                        for (let itemGroupRepeat in states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].items![itemGroupName]) {
                            newData[studyEventOid][studyEventRepeat][caseReportFormOid][itemGroupName][itemGroupRepeat] = {};
                            
                            for (let itemName in states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].items![itemGroupName][itemGroupRepeat]) {
                                newData[studyEventOid][studyEventRepeat][caseReportFormOid][itemGroupName][itemGroupRepeat][itemName] = {
                                    event: states[studyEventOid][studyEventRepeat].event!,
                                    form: states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].form!,
                                    item: states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].items![itemGroupName][itemGroupRepeat][itemName].point!,
                                    review: states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].items![itemGroupName][itemGroupRepeat][itemName].review,
                                    verification: states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].items![itemGroupName][itemGroupRepeat][itemName].verification,
                                    notApplicable: states[studyEventOid][studyEventRepeat].forms![caseReportFormOid].items![itemGroupName][itemGroupRepeat][itemName].notApplicable,
                                    reviewLoading: false,
                                    verificationLoading: false,
                                    notApplicableLoading: false,
                                };
                            }
                        }
                    }
                }
            }
        }

        return newData
    }, [states])

    const intialState = useMemo(() => {
        return {
            data,
            loading: statesLoading || statesValidating
        }
    }, [data, statesLoading])

    return (
        <CoreForm
            initialValue={intialState}
        >
            <FormErrorBoundary>
                <ReviewAndVerificationFormUpdater
                    trialId={trialId}
                    masterGroupId={masterGroupId}
                    coordinatingGroupId={coordinatingGroupId}
                    institutionId={institutionId}
                    participantId={participantId}
                    studyEventOid={studyEventOid}
                    studyEventRepeat={studyEventRepeat}
                    caseReportFormOid={caseReportFormOid}
                />
                <Field propertyPath="" />
                {children}
                {
                    debug && (
                        <CollapsiblePanel 
                            ComponentProps={{
                                title: 'Debug'
                            }}
                        >
                            <Box sx={{pl: 4}}>
                                <FormState />
                                <FormFields />
                                <FormInitialValue />
                                <FormValue />
                                <FormTouched />
                                <FormDirty />
                                <FormFocused />
                                <FormErrors />
                            </Box>
                        </CollapsiblePanel>
                    )
                }                      
            </FormErrorBoundary>
        </CoreForm>
    )
};

export default ReviewAndVerificationForm;